/**
 * Shared demo framework — hook-shim helper factory.
 *
 * In demo mode a domain's query hooks short-circuit to return fixture data
 * instead of the backend's. We do this WITHOUT skipping the real `useQuery`
 * call (Rules of Hooks): each hook always calls `useQuery`, then pipes the
 * result through `withDemoData()`, which — only when this demo is active —
 * replaces the query's `data`/loading/error fields with a synchronous fixture.
 *
 * Because demo mode also disables the query (`enabled: !demoActive()` layered
 * in by each shimmed hook), no network request fires; the override purely
 * supplies a resolved-success shape so real pages/components render unchanged.
 *
 *   const { demoActive, useFixture, withDemoData } = createHookShim('ap', apDemoStore);
 */

import type { UseQueryResult } from '@tanstack/react-query';
import { isDemoActive } from './demo-mode';
import type { DemoStore } from './create-demo-store';

export interface HookShim<TState> {
  /** True when this demo's query hooks should serve fixtures. */
  demoActive: () => boolean;
  /**
   * Reactively select a fixture slice when this demo is active. Returns
   * `undefined` when it isn't, so the caller falls through to the real query.
   * Always calls the underlying store hook (Rules of Hooks).
   */
  useFixture: <T>(selector: (s: TState) => T) => T | undefined;
  /**
   * Wrap a real `useQuery` result so that, when this demo is active, it presents
   * the provided fixture `data` as a resolved success. When inactive the
   * original query result passes through untouched.
   */
  withDemoData: <TData, TError>(
    query: UseQueryResult<TData, TError>,
    fixtureData: TData | undefined,
  ) => UseQueryResult<TData, TError>;
}

export function createHookShim<TState>(
  demoKey: string,
  store: DemoStore<TState>,
): HookShim<TState> {
  function demoActive(): boolean {
    return isDemoActive(demoKey);
  }

  function useFixture<T>(selector: (s: TState) => T): T | undefined {
    return store.useStore((s) =>
      isDemoActive(demoKey) ? selector(s) : undefined,
    );
  }

  function withDemoData<TData, TError>(
    query: UseQueryResult<TData, TError>,
    fixtureData: TData | undefined,
  ): UseQueryResult<TData, TError> {
    if (!isDemoActive(demoKey) || fixtureData === undefined) {
      return query;
    }

    return {
      ...query,
      data: fixtureData,
      error: null,
      isError: false,
      isLoading: false,
      isLoadingError: false,
      isPending: false,
      isSuccess: true,
      isFetched: true,
      isFetchedAfterMount: true,
      isFetching: false,
      isRefetching: false,
      isPlaceholderData: false,
      status: 'success',
      fetchStatus: 'idle',
    } as UseQueryResult<TData, TError>;
  }

  return { demoActive, useFixture, withDemoData };
}
